/* eslint-disable react-hooks/rules-of-hooks */
import { Popover } from "@mui/material";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  useFetchNotification,
  useSeenAllNotifications,
  useSeenNotification,
} from "../../setup/api/services/notifications";
import { INotificationsResponseDataContent } from "../../setup/api/services/notifications/models";
import { ROUTES } from "../../setup/constants";
import { AuthConfig } from "../../setup/context/config/authConfig";
import Avatar, { AvatarSize } from "../common/Avatar";
import { EmptyNotificationList } from "./empty";
import styles from "./styles.module.scss";
interface INotificationProps {
  anchorElNotification: HTMLDivElement | null;
  handlePopoverClose: () => void;
  elementId: string;
}

const Notification = (props: INotificationProps) => {
  const userId = AuthConfig.getUserInfo()?.id;
  const navigate = useNavigate();
  const [refresh, setRefresh] = useState(false);

  const { anchorElNotification, handlePopoverClose, elementId } = props;
  const onNotificationClick = async (event, notification) => {
    handleClose();
    if (!notification.seen) {
      await useSeenNotification(userId, [notification.id]);
      setRefresh(true);
    }
    const contractId = notification.contractId || notification.shortContractId;
    navigate(`${ROUTES.CONTRACT}${contractId}`);
  };

  const [pageNumber, setPageNumber] = useState(0);
  const size = 10;
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(false);
  const [notifications, setNotifications] = useState([] as any);

  const [hasMore, setHasMore] = useState(false);
  const { t } = useTranslation();

  const fetching = useRef(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    if (userId && Boolean(anchorElNotification) === true && !fetching.current) {
      fetching.current = true;
      useFetchNotification(userId, pageNumber, size)
        .then((res) => {
          setNotifications((prevNotifications) => {
            if (refresh === true) {
              setRefresh(false);
              setPageNumber(0);
              setHasMore(false);
              return [...res.data.content];
            } else return [...prevNotifications, ...res.data.content];
          });
          setHasMore(res.data.totalPages - pageNumber * size >= 1);
          setLoading(false);
          fetching.current = false;
        })
        .catch((e) => {
          setError(true);
        });
    } else if (!anchorElNotification) {
      fetching.current = false;
      setNotifications([]);
      setPageNumber(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, anchorElNotification]);

  const handleClose = () => {
    handlePopoverClose();
    setNotifications([]);
    setRefresh(true);
  };

  const observer = useRef<any>();
  const lastNotificationElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer?.current?.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prevPageNumber) => prevPageNumber + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const onSeenAll = async () => {
    await useSeenAllNotifications(userId);
    setRefresh(true);
  };

  return (
    <>
      <Popover
        id={elementId}
        open={Boolean(anchorElNotification)}
        anchorEl={anchorElNotification}
        className={classNames(styles["pop-over"])}
        onClose={handleClose}
        keepMounted
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          width: { md: "339px", xs: "100vw" },
          maxWidth: { md: "339px", xs: "100vw" },
          top: { md: "inherit", xs: "26px !important" },
          left: { md: "10%", xs: "0px !important" },
          right: { md: "10%", xs: "0px !important" },
        }}
        PaperProps={{
          sx: {
            width: { md: "415px", xs: "100vw" },
            maxWidth: { md: "415px", xs: "100vw" },
            top: { md: "inherit", xs: "46px !important" },
            left: { md: "213px !important", xs: "0px !important" },
            right: { md: "10% !important", xs: "0px !important" },
          },
        }}>
        <div className={styles["list"]}>
          <div className={styles.list_header}>
            <div className={styles["link"]} onClick={onSeenAll}>
              <p className={styles.p1}>
                {t("lbl_notifications_mark_all_as_read")}
              </p>
            </div>
            <div className={styles["notification-text"]}>
              <h2>{t("lbl_notification")}</h2>
            </div>
          </div>
          {notifications?.length > 0 ? (
            notifications.map(
              (
                notification: INotificationsResponseDataContent,
                index: number
              ) => {
                return (
                  <div
                    className={classNames(
                      styles.notification_item,
                      !notification.seen && styles.notification_item_seen,
                      notifications.length - 1 === index && styles.pb200
                    )}
                    key={index}
                    onClick={(event) =>
                      onNotificationClick(event, notification)
                    }
                    ref={lastNotificationElementRef}>
                    <p className={classNames("hint", styles["g900"])}>
                      {notification.message.slice(-1) === "."
                        ? notification.message.slice(0, -1)
                        : notification.message}
                      <p className={classNames(styles.hint, styles.g700)}>
                        {notification.title}
                      </p>
                      <p
                        className={classNames(
                          styles.hint,
                          styles.g500,
                          styles.mt8
                        )}>
                        {convertDate(notification.createdAt)}
                      </p>
                    </p>
                    <div className={styles.avatar_image}>
                      <Avatar
                        avatarSize={AvatarSize.MEDIUM}
                        src={notification.imageUrl}
                        alt=""
                      />
                    </div>
                    {!notification.seen && <div className={styles.dot} />}
                  </div>
                );
              }
            )
          ) : (
            <EmptyNotificationList />
          )}
          <div onClick={handleClose} className={styles.close_notification}>
            <img src="/assets/images/svg/cross_white_bg.svg" />
          </div>
        </div>
      </Popover>
    </>
  );
};

export const convertDate = (date: string) => {
  return new Date(date).toLocaleDateString("ar-eg");
};

export default Notification;
